import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI } from 'sonata-react-components';

import { sumSaveStepCounts } from '@/components/common/MultiFlightMap/MultiFlightMap.helpers';
import { EMPTY_SAVE_STEP_COUNTS, getSaveStepTotal } from '@/types/saveSteps.types';
import { CountLines, SaveGroupHeader } from './SaveProgressGroupRow';
import {
  ACTIVATION_AREA_COUNT_MESSAGES,
  ATTRIBUTION_POI_COUNT_MESSAGES,
  CATEGORY_ICON,
  CREATIVE_COUNT_MESSAGES,
  getCountLines,
  getWorstStatus,
  LOCATION_COUNT_MESSAGES,
  PROXIMITY_POINT_COUNT_MESSAGES,
  SCREEN_COUNT_MESSAGES,
  STEP_STATUS_ICON
} from './saveProgressTooltipContent.messages';

import type { MessageDescriptor } from 'react-intl';
import type { SaveStepCounts, SaveStepStatus, SaveSteps } from '@/types/saveSteps.types';
import type { CountAction } from './saveProgressTooltipContent.messages';

import './saveProgressTooltipContent.styles.scss';

type Props = {
  contextTargetingCount: number;
  creativesOtherCount: number;
  isDooh: boolean;
  isDriveToStore: boolean;
  isPreview: boolean;
  mainSettingsCount: number;
  onDismiss: () => void;
  saveSteps: SaveSteps;
};

function SaveProgressTooltipContent({
  contextTargetingCount,
  creativesOtherCount,
  isDooh,
  isDriveToStore,
  isPreview,
  mainSettingsCount,
  onDismiss,
  saveSteps
}: Props) {
  const { formatMessage } = useIntl();

  // Root groups default closed; their subgroups (if any) have no expand state
  // of their own — they're plain rows gated entirely by the root's own state.
  const [rootOpen, setRootOpen] = useState<Record<string, boolean>>({});
  const toggleRoot = (id: string) => setRootOpen(prev => ({ ...prev, [id]: !prev[id] }));

  const venuesLabel = isDriveToStore
    ? formatMessage({ defaultMessage: 'Attribution POIs', id: 'subHeader.saveProgress.venues.attribution' })
    : formatMessage({ defaultMessage: 'Proximity Points', id: 'subHeader.saveProgress.venues.proximity' });

  const attributionZonesLabel = isDriveToStore
    ? formatMessage({
        defaultMessage: 'Attribution Locations',
        id: 'subHeader.saveProgress.attributionZones.attribution'
      })
    : formatMessage({
        defaultMessage: 'Proximity Locations',
        id: 'subHeader.saveProgress.attributionZones.proximity'
      });

  // DOOH activates screens rather than drawn areas.
  const areasLabel = isDooh
    ? formatMessage({ defaultMessage: 'DOOH Inventory', id: 'subHeader.saveProgress.screens.dooh' })
    : formatMessage({ defaultMessage: 'Activation Areas', id: 'subHeader.saveProgress.screens' });

  const locationsScreensLabel = isDooh
    ? formatMessage({ defaultMessage: 'Screens', id: 'ioSettings.sidebar.screens' })
    : formatMessage({ defaultMessage: 'Locations', id: 'ioSettings.sidebar.locations' });

  const mainSettingsLabel = formatMessage({ defaultMessage: 'Main Settings', id: 'ioSettings.sidebar.mainSettings' });

  const contextTargetingLabel = formatMessage({
    defaultMessage: 'Context & Targeting',
    id: 'ioSettings.sidebar.contextTargeting'
  });

  const creativesLabel = formatMessage({ defaultMessage: 'Creatives', id: 'ioSettings.sidebar.creatives.title' });

  const locationsSubgroups: {
    counts: SaveStepCounts;
    id: string;
    label: string;
    messages: Record<CountAction, MessageDescriptor>;
    status: SaveStepStatus;
  }[] = [
    {
      counts: saveSteps.venues.counts,
      id: 'venues',
      label: venuesLabel,
      messages: isDriveToStore ? ATTRIBUTION_POI_COUNT_MESSAGES : PROXIMITY_POINT_COUNT_MESSAGES,
      status: saveSteps.venues.status
    },
    {
      counts: saveSteps.attributionZones.counts,
      id: 'attributionZones',
      label: attributionZonesLabel,
      messages: LOCATION_COUNT_MESSAGES,
      status: saveSteps.attributionZones.status
    },
    {
      counts: saveSteps.screens.counts,
      id: 'screens',
      label: areasLabel,
      messages: isDooh ? SCREEN_COUNT_MESSAGES : ACTIVATION_AREA_COUNT_MESSAGES,
      status: saveSteps.screens.status
    }
  ].filter(subgroup => subgroup.status !== 'disabled');

  const locationsScreensStatus = getWorstStatus(locationsSubgroups.map(subgroup => subgroup.status));
  const locationsScreensVisible = locationsScreensStatus !== 'disabled';
  // A group still queued (not yet started saving) looks like preview — the
  // category icon, not the generic "pending" dot — until it actually starts
  // processing.
  const locationsScreensLooksPending = isPreview || locationsScreensStatus === 'pending';
  const locationsScreensIcon = locationsScreensLooksPending
    ? CATEGORY_ICON.locationsScreens
    : STEP_STATUS_ICON[locationsScreensStatus];
  const locationsScreensTotal = getSaveStepTotal(
    locationsSubgroups.reduce((acc, subgroup) => sumSaveStepCounts(acc, subgroup.counts), EMPTY_SAVE_STEP_COUNTS)
  );

  const entityModelStatus = saveSteps.entityModel.status;
  const entityModelLooksPending = isPreview || entityModelStatus === 'pending';

  const mainSettingsVisible = mainSettingsCount > 0;
  const mainSettingsIcon = entityModelLooksPending ? CATEGORY_ICON.mainSettings : STEP_STATUS_ICON[entityModelStatus];

  const contextTargetingVisible = contextTargetingCount > 0;
  const contextTargetingIcon = entityModelLooksPending
    ? CATEGORY_ICON.contextTargeting
    : STEP_STATUS_ICON[entityModelStatus];

  const creativesTotal = getSaveStepTotal(saveSteps.entityModel.counts) + creativesOtherCount;
  const creativesVisible = creativesTotal > 0;
  const creativesIcon = entityModelLooksPending ? CATEGORY_ICON.creatives : STEP_STATUS_ICON[entityModelStatus];
  const creativesCountLines = [
    ...getCountLines(saveSteps.entityModel.counts, CREATIVE_COUNT_MESSAGES, formatMessage),
    ...(creativesOtherCount > 0
      ? [
          {
            id: 'dco',
            text: formatMessage({
              defaultMessage: 'Dynamic Creative Optimization settings updated',
              id: 'subHeader.saveProgress.creatives.dco'
            })
          }
        ]
      : [])
  ];

  const visibleRootStatuses: SaveStepStatus[] = [
    ...(locationsScreensVisible ? [locationsScreensStatus] : []),
    ...(mainSettingsVisible ? [entityModelStatus] : []),
    ...(contextTargetingVisible ? [entityModelStatus] : []),
    ...(creativesVisible ? [entityModelStatus] : [])
  ];
  const isFinished = visibleRootStatuses.every(status => status === 'success' || status === 'fail');
  const hasFailed = visibleRootStatuses.some(status => status === 'fail');
  const isWide = mainSettingsVisible || contextTargetingVisible || creativesVisible || hasFailed;

  // Preview assumed there was always something unsaved to show, which held
  // until Dismiss existed — dismissing a fully-successful save forces preview
  // mode back on even though nothing is actually pending anymore (the mouse
  // just hasn't left the button yet). With nothing to preview, show nothing.
  const hasAnyChanges = locationsScreensVisible || mainSettingsVisible || contextTargetingVisible || creativesVisible;
  if (isPreview && !hasAnyChanges) {
    // Not `null`: the Tooltip's own floating panel (padding/background/shadow
    // from `.subHeader__progressTooltip`) still renders around empty content,
    // showing as a tiny white blob. This marker lets that panel hide itself
    // via `:has()` instead.
    return <div className="saveProgressTooltipContent--empty" />;
  }

  // Dismiss is offered once the save has fully settled, whether it succeeded
  // or failed — while still processing there's nothing to dismiss yet.
  const showDismiss = !isPreview && isFinished;

  // A failure takes priority over "still finishing" — shown the moment any
  // step fails, even while other steps are still processing, rather than
  // waiting for everything to settle.
  const title = isPreview
    ? formatMessage({ defaultMessage: 'You have pending changes', id: 'subHeader.saveProgress.title.pending' })
    : hasFailed
      ? formatMessage({ defaultMessage: "Some changes couldn't be saved", id: 'subHeader.saveProgress.title.fail' })
      : isFinished
        ? formatMessage({ defaultMessage: "Everything's saved", id: 'subHeader.saveProgress.title.success' })
        : formatMessage({ defaultMessage: 'Saving changes...', id: 'subHeader.saveProgress.title' });

  const subtitle = isPreview
    ? formatMessage({
        defaultMessage: 'Save to apply the changes below.',
        id: 'subHeader.saveProgress.subtitle.pending'
      })
    : hasFailed
      ? formatMessage({
          defaultMessage: 'Try again, or contact support if this keeps happening.',
          id: 'subHeader.saveProgress.subtitle.fail'
        })
      : isFinished
        ? formatMessage({
            defaultMessage: 'You can dismiss this now.',
            id: 'subHeader.saveProgress.subtitle.success'
          })
        : formatMessage({
            defaultMessage: "Don't navigate away until this finishes.",
            id: 'subHeader.saveProgress.subtitle'
          });

  return (
    <div className={`saveProgressTooltipContent ${isWide ? 'wide' : ''}`}>
      <div className="saveProgressHeader">
        <p className="saveProgressTitle">
          <span className="saveProgressTitleLeft">{title}</span>
          {showDismiss && (
            <ButtonUI
              className="saveProgressTitleDismissButton"
              onClick={onDismiss}
              shape="link"
              size="ultraSmall"
              text={formatMessage({ defaultMessage: 'Dismiss', id: 'subHeader.saveProgress.title.dismiss' })}
              type="primary"
            />
          )}
        </p>
        {subtitle && <p className="saveProgressSubtitle">{subtitle}</p>}
      </div>
      <div className="saveSteps">
        {locationsScreensVisible && (
          <div className="saveStep">
            <SaveGroupHeader
              countTotal={locationsScreensTotal}
              icon={locationsScreensIcon}
              isOpen={!!rootOpen.locationsScreens}
              isPreview={locationsScreensLooksPending}
              label={locationsScreensLabel}
              onToggleOpen={() => toggleRoot('locationsScreens')}
              status={locationsScreensStatus}
            />
            {rootOpen.locationsScreens && (
              <div className="saveSubgroups">
                {locationsSubgroups.map(subgroup => (
                  <div className="saveStep saveStep--nested" key={subgroup.id}>
                    <SaveGroupHeader label={subgroup.label} status={subgroup.status} />
                    <CountLines
                      countLines={getCountLines(subgroup.counts, subgroup.messages, formatMessage)}
                      status={subgroup.status}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {mainSettingsVisible && (
          <div className="saveStep">
            <SaveGroupHeader
              countTotal={mainSettingsCount}
              icon={mainSettingsIcon}
              isPreview={entityModelLooksPending}
              label={mainSettingsLabel}
              status={entityModelStatus}
            />
          </div>
        )}
        {contextTargetingVisible && (
          <div className="saveStep">
            <SaveGroupHeader
              countTotal={contextTargetingCount}
              icon={contextTargetingIcon}
              isPreview={entityModelLooksPending}
              label={contextTargetingLabel}
              status={entityModelStatus}
            />
          </div>
        )}
        {creativesVisible && (
          <div className="saveStep">
            <SaveGroupHeader
              countTotal={creativesTotal}
              icon={creativesIcon}
              isOpen={!!rootOpen.creatives}
              isPreview={entityModelLooksPending}
              label={creativesLabel}
              onToggleOpen={() => toggleRoot('creatives')}
              status={entityModelStatus}
            />
            {rootOpen.creatives && <CountLines countLines={creativesCountLines} status={entityModelStatus} />}
          </div>
        )}
      </div>
    </div>
  );
}

export default SaveProgressTooltipContent;
